/* eslint-disable react/react-in-jsx-scope */
import { useMutation } from "@apollo/client";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import GeoAutoCompletion from "components/GeoAutoCompletion";
import Table from "components/Table";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { useCourseContext } from "contexts/CourseContext";
import { useClientAddressModal } from "domains/Course/data";
import { ICourseDelivery, ICourseModal, ICoursePickup, modalType as modal } from "domains/Course/data/types";
import { AddressModalHeader, mondalButtonsStyle, useAddressStyle } from "domains/Course/style";
import { fieldEmptyNull } from "domains/UserAddress/utils";
import { ClientRefsAddress } from "generated/graphql";
import { ADD_CLIENT_REF, UPDATE_CLIENT_REF_ADDRESS } from "graphql/Address/mutations";
import { ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID } from "graphql/clientRef/queries";
import { useCurrentClient } from "hooks";
import { memo, useCallback, useMemo, useState } from "react";
import Dialog from "ui/Dialog";
import { TextField } from "ui/TextField";
import { isValidEmail, isValidPhoneNumber } from "utils";
import { Divider } from "../Divider";

function AddressModal({ open, onConfirm, onClose, itemIndex, modalType, isPickup }: ICourseModal) {
  const classes = useAddressStyle();
  const { text } = useText();
  const { pickups, deliveries, clientAddresses, editPickup, editDelivery, isLoading } = useCourseContext();
  const { client } = useCurrentClient();
  const dispatchAlert = useAlertDispatch();
  const columns = useClientAddressModal(text);
  const [isFormModal, setIsFormModal] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [clientAddress, setClientAddress] = useState<ClientRefsAddress | any>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [addOrEditEntrepriseAddress, { loading: isEntrepriseAction }] = useMutation(
    isEdit ? UPDATE_CLIENT_REF_ADDRESS : ADD_CLIENT_REF
  );

  const onRadioButtonSelected = useCallback(
    (value: any) => {
      setClientAddress(null);
      modalType === modal?.PICKUP_MODAL
        ? editPickup(itemIndex, "address", value)
        : editDelivery(itemIndex, "deliveryAddress", value);
    },
    [pickups, deliveries, modalType]
  );

  const handleCloseForm = () => {
    onClose();
    setIsEdit(false);
  };

  const isOkTosave = useMemo(
    () =>
      !clientAddress?.firstname ||
      !clientAddress?.lastname ||
      !clientAddress?.phone_number ||
      !isValidPhoneNumber(clientAddress?.phone_number) ||
      !clientAddress?.email ||
      clientAddress?.address?.address === undefined,
    [clientAddress]
  );

  const availableAddress = useMemo(() => {
    const validIds = [...pickups]?.map((item: any) => item?.address?._id)?.filter(Boolean);

    const unavailableAddressesIds = clientAddresses
      ?.filter((item: any) => !validIds?.includes(item?._id))
      ?.map((item: any) => item?._id);

    const allAdressesIds = clientAddresses?.map((item: any) => item?._id);

    return isPickup ? unavailableAddressesIds : allAdressesIds;
  }, [pickups, deliveries, clientAddresses]);

  const onEditRow = useCallback(
    (value: any) => {
      const { phone_number, phoneNumber } = value;
      setIsEdit(true);
      setClientAddress({
        ...value,
      });
      setIsFormModal(true);
    },
    [modalType, pickups, deliveries]
  );

  const handleOpenForm = () => {
    setIsFormModal(!isFormModal);
    setClientAddress(null);
    setIsEdit(false);
  };

  const modalTitle = useMemo(
    () => (clientAddress?.address?.address === undefined ? text.course.new_address : text.course.edit_address),
    [clientAddress]
  );

  const currentSelectedAddress = () =>
    modalType === modal?.PICKUP_MODAL ? pickups[itemIndex]?.address?._id : deliveries[itemIndex]?.deliveryAddress?._id;

  const handleForm = {
    handleTextInput: (event: any) => {
      setClientAddress({ ...clientAddress, [event?.target?.name]: event?.target.value });
    },
    handleLocationInput: (value: any) => {
      setClientAddress({ ...clientAddress, address: value });
    },
    handlePhoneNumber: (value: string) => {
      setClientAddress({ ...clientAddress, phone_number: value });
    },
  };

  const handleEntrepriseAddress = async () => {
    const { __typename: type, address, _id: id, ...clientInfos } = clientAddress;
    const { description, country, __typename, _id, ...validAddress } = address as any;
    await addOrEditEntrepriseAddress({
      variables: {
        input: {
          clientAddress: {
            ...clientInfos,
            address: validAddress,
            default: isEdit ? clientInfos?.default : false,
          },
          [isEdit ? "id" : "clientID"]: isEdit ? id : client?._id,
        },
      },
      refetchQueries: [
        {
          query: ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID,
          variables: {
            params: {
              clientID: client?._id,
            },
          },
        },
      ],
    })
      .then(({ data }: any) => {
        if (data?.[isEdit ? "updateClientRefAddress" : "addClientRefAddress"]?.code === HTTP_STATUS_CODES?.CREATED) {
          setClientAddress(!isEdit ? data?.addClientRefAddress?.data : data?.updateClientRefAddress?.data);
          modalType === modal?.PICKUP_MODAL
            ? editPickup(itemIndex, "address", clientAddress)
            : editDelivery(itemIndex, "deliveryAddress", clientAddress);
          setIsFormModal(!isFormModal);
          setIsSubmitted(false);
          setIsEdit(false);
          dispatchAlert({
            message: isEdit ? text.all.edit_address_success : text.all.add_address_success,
            severity: "success",
          });
        }
      })
      .catch((e) => {
        dispatchAlert({
          message: text.all.something_wrong,
          severity: "error",
        });
      });
  };

  const editAllAddresses = (editedAddress: any) => {
    if (isPickup) {
      deliveries?.filter((delivery: ICourseDelivery, index: number) => {
        if (delivery?.pickupAddress?._id === editedAddress?._id) {
          editDelivery(index, "pickupAddress", editedAddress);
        }
      });
    } else {
      pickups?.filter((pickup: ICoursePickup, index: number) => {
        if (pickup?.address?._id === editedAddress?._id) {
          editPickup(index, "address", editedAddress);
        }
      });
    }
  };

  const handleSubmitForm = async () => {
    setIsSubmitted(true);
    if (!isOkTosave) {
      await handleEntrepriseAddress();
    }
  };

  const selectAddressMessage = useMemo(
    () => (isPickup ? text.course.select_course_pickup_address : text.course.select_course_delivery_address),
    [isPickup]
  );

  return (
    <div>
      <Dialog
        open={open}
        maxWidth="md"
        hideDialogHeader
        confirmButtonTitle={text.all.validate}
        cancelButtonTitle={text.all.cancel}
        handleClose={isFormModal ? handleOpenForm : handleCloseForm}
        confirmButtonStyle={mondalButtonsStyle}
        closeButtonStyle={mondalButtonsStyle}
        handleConfirm={isFormModal ? handleSubmitForm : onClose}
        disableConfirmButton={isEntrepriseAction}
        PrimaryButtonProps={
          isEntrepriseAction && {
            endIcon: <CircularProgress className={classes.progressBar} size={30} />,
          }
        }
      >
        {!isFormModal ? (
          <>
            <AddressModalHeader>
              <p className={classes.title}>{selectAddressMessage}</p>
              <Button className={classes.button} onClick={handleOpenForm}>
                {text.course.new_address}
              </Button>
            </AddressModalHeader>
            <div className={classes.table}>
              <Table
                tableHeight="auto"
                columns={columns || []}
                data={clientAddresses || []}
                rowsIdentifier="_id"
                isLoading={isLoading}
                isDisabledFooter
                countTotal={clientAddresses?.length}
                hasRadioButton
                rowScanned={[...availableAddress, currentSelectedAddress()] || []}
                onRadioButtonSelected={onRadioButtonSelected}
                onRowEdit={onEditRow}
                selectedRadionButton={clientAddress?._id || currentSelectedAddress()}
              />
            </div>
          </>
        ) : (
          <>
            <p className={classes.title}>{modalTitle}</p>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <GeoAutoCompletion
                  address={clientAddress?.address}
                  onChange={handleForm?.handleLocationInput}
                  error={isSubmitted && !clientAddress?.address?.address?.trim()}
                  helperText={
                    isSubmitted && !clientAddress?.address?.address?.trim() ? text.all.this_field_is_required : ""
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Divider prefix="Contact Infos" />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  variant="outlined"
                  label={text.all.first_name}
                  name="firstname"
                  onChange={handleForm?.handleTextInput}
                  value={clientAddress?.firstname}
                  fullWidth
                  error={isSubmitted && !clientAddress?.firstname?.trim()}
                  helperText={isSubmitted && !clientAddress?.firstname?.trim() ? text.all.this_field_is_required : ""}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  variant="outlined"
                  label={text.all.last_name}
                  name="lastname"
                  onChange={handleForm?.handleTextInput}
                  fullWidth
                  value={clientAddress?.lastname}
                  error={isSubmitted && !clientAddress?.lastname?.trim()}
                  helperText={isSubmitted && !clientAddress?.lastname?.trim() ? text.all.this_field_is_required : ""}
                />
              </Grid>

              <Grid item xs={6}>
                <PhoneField
                  required
                  label={text.all.phone_number}
                  name="phone_number"
                  onValueChange={handleForm?.handlePhoneNumber}
                  fullWidth
                  value={clientAddress?.phone_number}
                  error={
                    (isSubmitted &&
                      !clientAddress?.phone_number?.trim() &&
                      !isValidPhoneNumber(clientAddress?.phone_number?.trim())) ||
                    (isSubmitted && !isValidPhoneNumber(clientAddress?.phone_number?.trim()))
                  }
                  helperText={
                    isSubmitted && !fieldEmptyNull(clientAddress?.phone_number?.trim())
                      ? text.all.this_field_is_required
                      : isSubmitted && !isValidPhoneNumber(clientAddress?.phone_number?.trim() || "")
                      ? text.all.phone_number_format_error
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  variant="outlined"
                  label={text.all.email}
                  name="email"
                  onChange={handleForm?.handleTextInput}
                  fullWidth
                  value={clientAddress?.email}
                  error={isSubmitted && !clientAddress?.email?.trim() && !isValidEmail(clientAddress?.email)}
                  helperText={
                    isSubmitted && !clientAddress?.email?.trim() && !isValidEmail(clientAddress?.email)
                      ? text.all.this_field_is_required
                      : ""
                  }
                />
              </Grid>
            </Grid>
          </>
        )}
      </Dialog>
    </div>
  );
}

export default memo(AddressModal);
